"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { RuntimeLink, useLocale, useRuntimeNavigate } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

/** Props for the HeaderSearchForm component. */
export interface HeaderSearchFormProps {
  /** Whether to show the full search form (true) or a compact icon link (false). */
  showForm: boolean;
}

/**
 * Desktop header search form with keyword suggestions, or a compact search
 * icon link.
 *
 * Behaviour:
 *  - Clicking the search icon always navigates to /search — with the typed
 *    query when present, to a bare /search (full index landing) otherwise.
 *    The previous "pointer-events-none" decorative icon was unintuitive.
 *  - Suggestions are fetched from /api/search/keywords on focus + on every
 *    keystroke (300ms debounce), so the panel narrows as the user types.
 *  - Panel is a clean vertical list (one item per line), each row shows a
 *    leading magnifier glyph + the suggestion with the typed substring
 *    bolded, hover/focus state, keyboard support implicit via mouse.
 */
export function HeaderSearchForm({ showForm }: HeaderSearchFormProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const keywordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keywordAbortRef = useRef<AbortController | null>(null);
  const keywordCacheRef = useRef<Record<string, string[]>>({});
  const navigate = useRuntimeNavigate();
  const locale = useLocale();

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  const loadKeywords = async (query: string) => {
    keywordAbortRef.current?.abort();
    const controller = new AbortController();
    keywordAbortRef.current = controller;
    try {
      const langParam = locale && locale !== "fr" ? `lang=${encodeURIComponent(locale)}` : "";
      const url = query
        ? `/api/search/keywords?q=${encodeURIComponent(query)}${langParam ? `&${langParam}` : ""}`
        : `/api/search/keywords${langParam ? `?${langParam}` : ""}`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;
      const payload = await response.json();
      if (!Array.isArray(payload?.keywords)) return;
      const filtered = payload.keywords.filter((k: unknown): k is string => typeof k === "string");
      keywordCacheRef.current[query] = filtered;
      if (searchQuery.trim() === query) setSearchKeywords(filtered);
    } catch (error) {
      if ((error as Error).name !== "AbortError") setSearchKeywords([]);
    }
  };

  useEffect(() => {
    if (!searchFocused) {
      if (keywordTimerRef.current) { clearTimeout(keywordTimerRef.current); keywordTimerRef.current = null; }
      keywordAbortRef.current?.abort();
      return;
    }

    const trimmed = searchQuery.trim();
    const cached = keywordCacheRef.current[trimmed];
    if (cached) {
      setSearchKeywords(cached);
      return;
    }
    if (keywordTimerRef.current) clearTimeout(keywordTimerRef.current);
    // Empty input -> popular keywords (no debounce); typed input -> 300ms
    // debounce so we don't fire a request on every keystroke.
    keywordTimerRef.current = setTimeout(() => void loadKeywords(trimmed), trimmed ? 300 : 0);
    return () => {
      if (keywordTimerRef.current) { clearTimeout(keywordTimerRef.current); keywordTimerRef.current = null; }
    };
  }, [searchFocused, searchQuery]);

  const visibleKeywords = useMemo(() => searchKeywords.slice(0, 6), [searchKeywords]);

  if (!showForm) {
    return (
      <RuntimeLink
        href="/search"
        className="hidden min-[765px]:inline-flex items-center justify-center rounded-full p-2 text-[#005CA9] hover:bg-[#005CA9]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#005CA9]"
        aria-label={t(locale, "openSearch")}
      >
        <Search className="h-[18px] w-[18px] text-[#005CA9]" />
      </RuntimeLink>
    );
  }

  return (
    <form
      className="relative hidden min-[980px]:flex min-w-0 flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        submitSearch(searchQuery);
      }}
    >
      <label className="sr-only" htmlFor="header-search">{t(locale, "search")}</label>
      <input
        id="header-search"
        className="bcc-search-input h-8 w-full max-w-[16rem] min-[980px]:max-w-[12rem] rounded-full px-4 pr-10 text-sm xl:text-base font-semibold text-[#005CA9] placeholder:font-semibold placeholder:text-[#005CA9]/70 outline-none shadow-[inset_0_1px_1.5px_rgba(0,0,0,0.08)] focus:ring-2 focus:ring-[#005CA9]/25"
        style={{ backgroundColor: "rgba(0, 149, 219, 0.10)" }}
        placeholder={t(locale, "search")}
        autoComplete="off"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        onFocus={() => setSearchFocused(true)}
        // Delay blur so a click on a suggestion fires before the panel hides.
        onBlur={() => setTimeout(() => setSearchFocused(false), 120)}
      />
      <button
        type="submit"
        aria-label={t(locale, "search")}
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#005CA9] hover:bg-[#005CA9]/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#005CA9]"
      >
        <Search size={16} />
      </button>
      {searchFocused && visibleKeywords.length > 0 && (
        <SuggestionPanel
          query={searchQuery.trim()}
          suggestions={visibleKeywords}
          onSelect={submitSearch}
        />
      )}
    </form>
  );
}

/**
 * Vertical suggestion list — one row per item, each row shows a small
 * magnifier glyph and the suggestion with the matched substring bolded.
 * Hover/active state matches the rest of the brand (primary blue, soft
 * blue tint background on hover).
 *
 * `onMouseDown` preventDefault stops the input from losing focus before
 * the click registers (which would dismiss the panel mid-click).
 */
function SuggestionPanel({
  query,
  suggestions,
  onSelect,
}: {
  query: string;
  suggestions: string[];
  onSelect: (value: string) => void;
}) {
  const normalisedQuery = useMemo(() => normaliseForMatch(query), [query]);
  return (
    <div
      role="listbox"
      className="absolute left-0 top-full z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-[#005CA9]/15 bg-white text-[#0F2849] shadow-lg"
    >
      <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[#0F2849]/75">
        {query ? "Suggestions" : "Recherches populaires"}
      </div>
      <ul className="py-1">
        {suggestions.map((keyword) => (
          <li key={keyword}>
            <button
              type="button"
              role="option"
              aria-selected={false}
              className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#0F2849] transition hover:bg-[#005CA9]/8 focus:bg-[#005CA9]/10 focus:outline-none"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSelect(keyword)}
            >
              <Search size={14} className="shrink-0 text-[#005CA9]/70" />
              <span className="truncate">
                {renderHighlighted(keyword, normalisedQuery)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Strips diacritics + lowercases — same rule as the server scoring. */
function normaliseForMatch(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 * Wraps the substring of `label` that matches the user's normalised query
 * in `<strong>`. Walks the ORIGINAL label so accented characters render
 * untouched, comparing positions against the NORMALISED label. Returns the
 * label verbatim when there's no match (or when the query is empty —
 * popular-suggestion case).
 */
function renderHighlighted(label: string, normalisedQuery: string) {
  if (!normalisedQuery) return label;
  const normLabel = normaliseForMatch(label);
  const idx = normLabel.indexOf(normalisedQuery);
  if (idx < 0) return label;
  const end = idx + normalisedQuery.length;
  return (
    <>
      {label.slice(0, idx)}
      <strong className="font-semibold text-[#005CA9]">{label.slice(idx, end)}</strong>
      {label.slice(end)}
    </>
  );
}
